import { useState, useMemo } from 'react';
import { Input } from '@kollektive/design-system';
import { Search } from 'lucide-react';
import { ConnectionTypeCard } from './ConnectionTypeCard';
import {
  CONNECTION_TYPES,
  CONNECTION_CATEGORIES,
  type ConnectionTypeDefinition,
  type ConnectionCategory,
} from './connectionTypeRegistry';
import styles from './ConnectionTypeCatalog.module.css';

interface ConnectionTypeCatalogProps {
  onSelect: (type: ConnectionTypeDefinition) => void;
}

export function ConnectionTypeCatalog({ onSelect }: ConnectionTypeCatalogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<ConnectionCategory | 'all'>('all');

  const filteredTypes = useMemo(() => {
    return CONNECTION_TYPES.filter((type) => {
      const matchesCategory = activeCategory === 'all' || type.category === activeCategory;
      const matchesSearch =
        !searchQuery ||
        type.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        type.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [searchQuery, activeCategory]);

  return (
    <div className={styles.catalog}>
      <div className={styles.sidebar}>
        <Input
          placeholder="Search connectors..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          iconLeft={<Search size={16} />}
          className={styles.searchInput}
        />
        <div className={styles.categories}>
          {CONNECTION_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              className={`${styles.categoryButton} ${activeCategory === cat.id ? styles.active : ''}`}
              onClick={() => setActiveCategory(cat.id)}
              type="button"
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>
      <div className={styles.grid}>
        {filteredTypes.length > 0 ? (
          filteredTypes.map((type) => (
            <ConnectionTypeCard key={type.id} type={type} onClick={onSelect} />
          ))
        ) : (
          <div className={styles.empty}>
            <p className={styles.emptyText}>No connectors match your search</p>
          </div>
        )}
      </div>
    </div>
  );
}
